import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Button from "../../components/commons/Button";
import BannerTypeTabs from "../../components/partials/banners/BannerTypeTabs";
import BannersCarousel from "../../components/partials/banners/BannersCarousel";
import BannerForm from "../../components/partials/banners/BannerForm";
import { useAdminLoading } from "../../context/AdminLoadingContext";
import { useInitialPageLoad } from "../../components/layouts/admin-layout/useInitialPageLoad";
import {
  getTopBanners,
  getContentBanners,
} from "../../services/webServices/banners/get";
import {
  deleteTopBanner,
  deleteContentBanner,
} from "../../services/webServices/banners/delete";
import {
  CONTENT_TYPES,
  CONTENT_TYPE_LABELS,
  type AnyBanner,
  type BannerSection,
  type ContentBanner,
  type ContentType,
  type TopBanner,
} from "../../services/webServices/banners/types";

type ActiveForm = {
  section: BannerSection;
  banner: AnyBanner | null;
};

export default function Banners() {
  const [topBanners, setTopBanners] = useState<TopBanner[]>([]);
  const [floatingBanners, setFloatingBanners] = useState<ContentBanner[]>([]);
  const [selectedType, setSelectedType] = useState<ContentType>(
    CONTENT_TYPES[0],
  );
  const [activeForm, setActiveForm] = useState<ActiveForm | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isTopLoading, setIsTopLoading] = useState(false);

  const { showLoading, hideLoading } = useAdminLoading();
  const isFirstLoad = useInitialPageLoad();

  const fetchTopBanners = useCallback(async () => {
    if (isFirstLoad) showLoading();
    setIsTopLoading(true);
    try {
      const data = await getTopBanners();
      setTopBanners(data);
    } finally {
      setIsTopLoading(false);
      if (isFirstLoad) hideLoading();
    }
  }, [isFirstLoad, showLoading, hideLoading]);

  const fetchFloating = useCallback(async () => {
    setIsLoading(true);
    const data = await getContentBanners(selectedType);
    setFloatingBanners(data);
    setIsLoading(false);
  }, [selectedType]);

  useEffect(() => {
    void fetchTopBanners();
  }, [fetchTopBanners]);

  useEffect(() => {
    void fetchFloating();
  }, [fetchFloating]);

  useEffect(() => {
    if (!activeForm) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActiveForm(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeForm]);

  const handleTypeChange = (next: ContentType) => {
    setSelectedType(next);
    setActiveForm(null);
  };

  const handleSaved = () => {
    setActiveForm(null);
    void fetchTopBanners();
    void fetchFloating();
  };

  const handleEdit = (banner: AnyBanner, section: BannerSection) => {
    setActiveForm({ section, banner });
  };

  const handleCreate = (section: BannerSection) => {
    setActiveForm({ section, banner: null });
  };

  const handleDelete = async (uuid: string, section: BannerSection) => {
    const confirmed = window.confirm("آیا از حذف این بنر مطمئن هستید؟");
    if (!confirmed) return;

    if (section === "top") {
      const success = await deleteTopBanner({ uuid });
      if (!success) {
        toast.error("حذف بنر با مشکل مواجه شد");
        return;
      }
      setTopBanners((prev) => prev.filter((b) => b.uuid !== uuid));
    } else {
      const success = await deleteContentBanner({ uuid, contentType: selectedType });
      if (!success) {
        toast.error("حذف بنر با مشکل مواجه شد");
        return;
      }
      setFloatingBanners((prev) => prev.filter((b) => b.uuid !== uuid));
    }

    toast.success("بنر با موفقیت حذف شد");
  };

  const topForType = topBanners.filter((b) => b.banner_type === selectedType);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">بنرها</h1>
        <span className="text-sm text-gray-500">
          {CONTENT_TYPE_LABELS[selectedType]}
        </span>
      </div>

      <BannerTypeTabs value={selectedType} onChange={handleTypeChange} />

      <section>
        <div className="flex items-center justify-end mb-3">
          <Button
            variant="primary"
            style="solid"
            size="sm"
            text="ایجاد بنر بالایی"
            loading={false}
            disabled={false}
            onClick={() => handleCreate("top")}
          />
        </div>

        <BannersCarousel
          section="top"
          title="بنرهای بالایی"
          items={topForType}
          isLoading={isTopLoading}
          onEdit={(b) => handleEdit(b, "top")}
          onDelete={(uuid) => handleDelete(uuid, "top")}
        />
      </section>

      <section>
        <div className="flex items-center justify-end mb-3">
          <Button
            variant="primary"
            style="solid"
            size="sm"
            text="ایجاد بنر شناور"
            loading={false}
            disabled={false}
            onClick={() => handleCreate("floating")}
          />
        </div>

        <BannersCarousel
          section="floating"
          title="بنرهای شناور"
          items={floatingBanners}
          isLoading={isLoading}
          onEdit={(b) => handleEdit(b, "floating")}
          onDelete={(uuid) => handleDelete(uuid, "floating")}
        />
      </section>

      {activeForm && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={() => setActiveForm(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <BannerForm
              section={activeForm.section}
              contentType={selectedType}
              editingBanner={activeForm.banner}
              onSaved={handleSaved}
              onCancel={() => setActiveForm(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
